import React, { useState, useEffect } from "react";
import { Box, Typography } from "@mui/material";
import liff from "../../lib/liff";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";
import { useUrlParams } from "hooks/useUrlParams";
import { getRequestFormUrl, memoryRollbackUrls } from "constants/links";

import { useSoulBackend } from "backend";
import ButtonDialog from "../components/ButtonDialog";
import Loading from "../components/Loading";
import MyPageCard from "../components/MyPageCard";
import StyledButton from "../components/StyledButton";
import FaqButton from "../components/FaqButton";

import { COLORS } from "constants/colors";
import AppLayout from "../components/AppLayout";
import Error from "../components/Error";
import TicketNotFound from "../components/TicketNotFound";
import { modeDescriptionUrls } from "constants/links";
import { useTheme } from "@emotion/react";

// Helper functions
const withLng = (path, lng) => {
  const hasQuery = path.includes("?");
  return lng ? `${path}${hasQuery ? "&" : "?"}lng=${lng}` : path;
};

const formatDateTime = (isoString, lng) => {
  const zone = lng === "en" ? "Europe/Warsaw" : "Asia/Tokyo";
  const dt = DateTime.fromISO(isoString, { zone: "utc" })
    .setZone(zone)
    .setLocale(lng);

  if (lng === "en") {
    return dt.toFormat("MMM d, yyyy HH:mm");
  } else {
    return dt.toFormat("yyyy/MM/dd HH:mm");
  }
};

// Custom hook for user episode management
const useUserEpisode = (soulId, backend) => {
  const [userEpisode, setUserEpisode] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [afterPurchase, setAfterPurchase] = useState(true);
  const [isError, setIsError] = useState(false);

  const initUserEpisode = async () => {
    try {
      const userEpisodeData = await backend.getEpisode(soulId);
      setUserEpisode(userEpisodeData);

      if (userEpisodeData === null) {
        setAfterPurchase(false);
      }
    } catch (error) {
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (soulId) {
      initUserEpisode();
    }
  }, [soulId, backend]);

  return { userEpisode, isLoading, afterPurchase, isError };
};

// Custom hook for mode switching
const useModeSwitch = (userEpisode, soulId, t, lng, backend) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [supportsModeSwitch, setSupportsModeSwitch] = useState(false);
  const [canScheduleModeSwitch, setCanScheduleModeSwitch] = useState(false);
  const [isScheduled, setIsScheduled] = useState(false);
  const [scheduleModeSwitchDescription, setScheduleModeSwitchDescription] =
    useState("");
  const [unscheduleModeSwitchDescription, setUnscheduleModeSwitchDescription] =
    useState("");

  // Dialog states
  const [openScheduleModeSwitchConfirm, setOpenScheduleModeSwitchConfirm] =
    useState(false);
  const [openScheduleModeSwitchComplete, setOpenScheduleModeSwitchComplete] =
    useState(false);
  const [openScheduleModeSwitchError, setOpenScheduleModeSwitchError] =
    useState(false);
  const [
    openScheduleModeSwitchUnavailable,
    setOpenScheduleModeSwitchUnavailable,
  ] = useState(false);
  const [openUnscheduleModeSwitchConfirm, setOpenUnscheduleModeSwitchConfirm] =
    useState(false);
  const [
    openUnscheduleModeSwitchComplete,
    setOpenUnscheduleModeSwitchComplete,
  ] = useState(false);
  const [openUnscheduleModeSwitchError, setOpenUnscheduleModeSwitchError] =
    useState(false);

  const createScheduleModeSwitchDescription = (modeInfo) => {
    const mode_after_switch = t(`common:mode.${modeInfo.mode_after_switch}`);
    const remaining_days = modeInfo.remaining_days_after_switch;
    const switch_at = formatDateTime(modeInfo.switch_at, lng);

    return t(`common:button.description.schedule_mode_switch`, {
      mode_after_switch,
      remaining_days,
      switch_at,
    });
  };

  const createUnscheduleModeSwitchDescription = (modeInfo) => {
    const mode_after_switch = t(`common:mode.${modeInfo.mode_after_switch}`);
    const switch_at = formatDateTime(modeInfo.switch_at, lng);

    return t(`common:button.description.unschedule_mode_switch`, {
      mode_after_switch,
      switch_at,
    });
  };

  const handleClickModeScheduleSwitch = () => {
    if (!canScheduleModeSwitch && isScheduled) {
      setOpenUnscheduleModeSwitchConfirm(true);
      return;
    }
    if (canScheduleModeSwitch) {
      setOpenScheduleModeSwitchConfirm(true);
      return;
    }
    if (!canScheduleModeSwitch) {
      setOpenScheduleModeSwitchUnavailable(true);
      return;
    }
  };

  const handleClickScheduleModeSwitchConfirm = async () => {
    try {
      setIsProcessing(true);
      await backend.scheduleModeSwitch(soulId);
      setOpenScheduleModeSwitchComplete(true);
      setCanScheduleModeSwitch(false);
      setIsScheduled(true);
    } catch (error) {
      setOpenScheduleModeSwitchError(true);
    } finally {
      setOpenScheduleModeSwitchConfirm(false);
      setIsProcessing(false);
    }
  };

  const handleClickUnscheduleModeSwitchConfirm = async () => {
    try {
      setIsProcessing(true);
      await backend.unscheduleModeSwitch(soulId);
      setOpenUnscheduleModeSwitchComplete(true);
      setCanScheduleModeSwitch(true);
      setIsScheduled(false);
    } catch (error) {
      setOpenUnscheduleModeSwitchError(true);
    } finally {
      setOpenUnscheduleModeSwitchConfirm(false);
      setIsProcessing(false);
    }
  };

  useEffect(() => {
    // モード切り替えの無い Soul（System など）は説明を作らない
    if (userEpisode?.mode_info?.supports_mode_switch) {
      setSupportsModeSwitch(
        userEpisode.mode_info.supports_mode_switch || false
      );
      setCanScheduleModeSwitch(userEpisode.mode_info.can_schedule);
      setIsScheduled(userEpisode.mode_info.is_scheduled);
      setScheduleModeSwitchDescription(
        createScheduleModeSwitchDescription(userEpisode.mode_info)
      );
      setUnscheduleModeSwitchDescription(
        createUnscheduleModeSwitchDescription(userEpisode.mode_info)
      );
    }
  }, [userEpisode, t, lng]);

  return {
    isProcessing,
    supportsModeSwitch,
    canScheduleModeSwitch,
    isScheduled,
    scheduleModeSwitchDescription,
    unscheduleModeSwitchDescription,
    dialogStates: {
      openScheduleModeSwitchConfirm,
      setOpenScheduleModeSwitchConfirm,
      openScheduleModeSwitchComplete,
      setOpenScheduleModeSwitchComplete,
      openScheduleModeSwitchError,
      setOpenScheduleModeSwitchError,
      openScheduleModeSwitchUnavailable,
      setOpenScheduleModeSwitchUnavailable,
      openUnscheduleModeSwitchConfirm,
      setOpenUnscheduleModeSwitchConfirm,
      openUnscheduleModeSwitchComplete,
      setOpenUnscheduleModeSwitchComplete,
      openUnscheduleModeSwitchError,
      setOpenUnscheduleModeSwitchError,
    },
    handlers: {
      handleClickModeScheduleSwitch,
      handleClickScheduleModeSwitchConfirm,
      handleClickUnscheduleModeSwitchConfirm,
    },
  };
};

// Custom hook for reset functionality
const useResetMemory = (userEpisode, soulId) => {
  const [hasResetOptions, setHasResetOptions] = useState(false);
  const [canSoftReset, setCanSoftReset] = useState(false);
  const [openResetConfirm, setResetOpenConfirm] = useState(false);
  const [openResetNotice, setResetOpenNotice] = useState(false);
  const [openComplete, setOpenComplete] = useState(false);
  const [openResetError, setResetOpenError] = useState(false);

  const handleResetClickConfirm = async (resetType = "hard") => {
    try {
      setResetOpenConfirm(false);
      const messageText = resetType === "soft" ? "**sreset**" : "**reset**";
      liff.sendMessages([{ type: "text", text: messageText }]);
      liff.closeWindow();
      setOpenComplete(true);
    } catch (error) {
      setResetOpenError(true);
    }
  };

  const onClickResetMemory = () => {
    if (userEpisode?.can_reset) {
      setResetOpenConfirm(true);
    } else {
      setResetOpenNotice(true);
    }
  };

  useEffect(() => {
    if (userEpisode) {
      setHasResetOptions(userEpisode.has_reset_options || false);
      setCanSoftReset(userEpisode.can_soft_reset || false);
    }
  }, [userEpisode]);

  return {
    hasResetOptions,
    canSoftReset,
    dialogStates: {
      openResetConfirm,
      setResetOpenConfirm,
      openResetNotice,
      setResetOpenNotice,
      openComplete,
      setOpenComplete,
      openResetError,
      setResetOpenError,
    },
    handlers: {
      onClickResetMemory,
      handleResetClickConfirm,
    },
  };
};

// Component for character situation (button + dialog)
const CharacterSituationSection = ({ t, work, character, isProcessing }) => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <StyledButton
        variant="contained"
        onClick={() => setOpen(true)}
        sx={{
          marginTop: 4,
          background: isProcessing ? "white" : "transparent",
        }}
      >
        {t("common:button.character_situation")}
      </StyledButton>

      <ButtonDialog
        open={open}
        title={t(`${work}:characterSituation.${character}.title`)}
        description={
          <div>{t(`${work}:characterSituation.${character}.description`)}</div>
        }
        handleClose={() => setOpen(false)}
      />
    </>
  );
};

// Component for reset memory (button + dialogs)
const ResetMemorySection = ({ userEpisode, resetMemory, t, isProcessing }) => {
  if (userEpisode.is_expired) return null;

  return (
    <>
      <StyledButton
        variant="contained"
        disabled={isProcessing}
        onClick={resetMemory.handlers.onClickResetMemory}
        sx={{
          marginTop: 4,
          background: isProcessing ? "white" : "transparent",
        }}
      >
        {t("common:button.reset_memories")}
      </StyledButton>

      {/* Reset confirmation dialog */}
      <ButtonDialog
        open={resetMemory.dialogStates.openResetConfirm}
        title={
          resetMemory.hasResetOptions
            ? t("common:button.dialog.reset_memories_with_options_confirm")
            : t("common:button.dialog.reset_memories_confirm")
        }
        warnings={t("common:button.warning.reset_memories", {
          returnObjects: true,
        })}
        buttons={[
          {
            label: resetMemory.hasResetOptions
              ? t("common:button.hard_reset")
              : t("common:button.reset"),
            onClick: () => resetMemory.handlers.handleResetClickConfirm("hard"),
          },
          ...(resetMemory.hasResetOptions
            ? [
              {
                label: t("common:button.soft_reset"),
                onClick: () =>
                  resetMemory.handlers.handleResetClickConfirm("soft"),
                disabled: !resetMemory.canSoftReset,
              },
            ]
            : []),
        ]}
        buttonNotice={
          resetMemory.hasResetOptions
            ? t("common:button.dialog.reset_memories_with_options_notice")
            : ""
        }
        handleClose={() => resetMemory.dialogStates.setResetOpenConfirm(false)}
      />

      {/* Reset success dialog */}
      <ButtonDialog
        open={resetMemory.dialogStates.openComplete}
        title={`${t("common:button.dialog.reset_memories_success")}`}
        buttons={[
          {
            label: `${t("common:button.close")}`,
            onClick: () => resetMemory.dialogStates.setOpenComplete(false),
          },
        ]}
        handleClose={() => resetMemory.dialogStates.setOpenComplete(false)}
      />

      {/* Reset notice dialog */}
      <ButtonDialog
        open={resetMemory.dialogStates.openResetNotice}
        title={t("common:button.dialog.cannot_reset_memories")}
        handleClose={() => resetMemory.dialogStates.setResetOpenNotice(false)}
        buttons={[
          {
            label: `${t("common:button.close")}`,
            onClick: () => resetMemory.dialogStates.setResetOpenNotice(false),
          },
        ]}
      />

      {/* Reset error dialog */}
      <ButtonDialog
        open={resetMemory.dialogStates.openResetError}
        title={`${t("common:button.dialog.reset_memories_fail")}`}
        buttons={[
          {
            label: `${t("common:button.close")}`,
            onClick: () => resetMemory.dialogStates.setResetOpenError(false),
          },
        ]}
        handleClose={() => resetMemory.dialogStates.setResetOpenError(false)}
      />
    </>
  );
};

// Component for mode switch (button + dialogs)
const ModeSwitchSection = ({
  userEpisode,
  modeSwitch,
  t,
  lng,
  isProcessing,
}) => {
  if (userEpisode.is_expired || !userEpisode.mode_info?.supports_mode_switch) {
    return null;
  }

  return (
    <>
      <StyledButton
        variant="contained"
        onClick={modeSwitch.handlers.handleClickModeScheduleSwitch}
        sx={{
          marginTop: 4,
          background: isProcessing ? "white" : "transparent",
        }}
      >
        {t("common:button.schedule_mode_switch")}
      </StyledButton>

      {/* Schedule mode switch confirm dialog */}
      <ButtonDialog
        open={modeSwitch.dialogStates.openScheduleModeSwitchConfirm}
        title={t("common:button.dialog.schedule_mode_switch_confirm")}
        description={
          <>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                marginBottom: 12,
              }}
            >
              <a
                href={modeDescriptionUrls[lng]}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: COLORS.SKY_BLUE,
                  textDecoration: "none",
                  fontWeight: 500,
                  fontSize: 13,
                }}
              >
                {t("common:button.description.what_is_mode")}
              </a>
            </div>
            <div style={{ textAlign: "center", marginBottom: 0 }}>
              {modeSwitch.scheduleModeSwitchDescription}
            </div>
          </>
        }
        buttons={[
          {
            label: `${t("common:button.schedule")}`,
            onClick: modeSwitch.handlers.handleClickScheduleModeSwitchConfirm,
            disabled: modeSwitch.isProcessing,
          },
        ]}
        handleClose={() =>
          modeSwitch.dialogStates.setOpenScheduleModeSwitchConfirm(false)
        }
      />
      <ButtonDialog
        open={modeSwitch.dialogStates.openScheduleModeSwitchComplete}
        title={`${t("common:button.dialog.schedule_mode_switch_success")}`}
        buttons={[
          {
            label: `${t("common:button.close")}`,
            onClick: () =>
              modeSwitch.dialogStates.setOpenScheduleModeSwitchComplete(false),
          },
        ]}
        handleClose={() =>
          modeSwitch.dialogStates.setOpenScheduleModeSwitchComplete(false)
        }
      />
      <ButtonDialog
        open={modeSwitch.dialogStates.openScheduleModeSwitchError}
        title={`${t("common:button.dialog.schedule_mode_switch_fail")}`}
        buttons={[
          {
            label: `${t("common:button.close")}`,
            onClick: () =>
              modeSwitch.dialogStates.setOpenScheduleModeSwitchError(false),
          },
        ]}
        handleClose={() =>
          modeSwitch.dialogStates.setOpenScheduleModeSwitchError(false)
        }
      />

      {/* モード切り替え予約削除 */}
      <ButtonDialog
        open={modeSwitch.dialogStates.openUnscheduleModeSwitchConfirm}
        title={t("common:button.dialog.unschedule_mode_switch_confirm")}
        description={
          <>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                marginBottom: 12,
              }}
            >
              <a
                href={modeDescriptionUrls[lng]}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: COLORS.SKY_BLUE,
                  textDecoration: "none",
                  fontWeight: 500,
                  fontSize: 13,
                }}
              >
                {t("common:button.description.what_is_mode")}
              </a>
            </div>
            <div style={{ textAlign: "center", marginBottom: 0 }}>
              {modeSwitch.unscheduleModeSwitchDescription}
            </div>
          </>
        }
        buttons={[
          {
            label: `${t("common:button.unschedule")}`,
            onClick: modeSwitch.handlers.handleClickUnscheduleModeSwitchConfirm,
            disabled: modeSwitch.isProcessing,
          },
        ]}
        handleClose={() =>
          modeSwitch.dialogStates.setOpenUnscheduleModeSwitchConfirm(false)
        }
      />
      <ButtonDialog
        open={modeSwitch.dialogStates.openUnscheduleModeSwitchComplete}
        title={`${t("common:button.dialog.unschedule_mode_switch_success")}`}
        buttons={[
          {
            label: `${t("common:button.close")}`,
            onClick: () =>
              modeSwitch.dialogStates.setOpenUnscheduleModeSwitchComplete(
                false
              ),
          },
        ]}
        handleClose={() =>
          modeSwitch.dialogStates.setOpenUnscheduleModeSwitchComplete(false)
        }
      />
      <ButtonDialog
        open={modeSwitch.dialogStates.openUnscheduleModeSwitchError}
        title={`${t("common:button.dialog.unschedule_mode_switch_fail")}`}
        buttons={[
          {
            label: `${t("common:button.close")}`,
            onClick: () =>
              modeSwitch.dialogStates.setOpenUnscheduleModeSwitchError(false),
          },
        ]}
        handleClose={() =>
          modeSwitch.dialogStates.setOpenUnscheduleModeSwitchError(false)
        }
      />

      {/* モード切り替え予約不可（事前制限） */}
      <ButtonDialog
        open={modeSwitch.dialogStates.openScheduleModeSwitchUnavailable}
        title={`${t("common:button.dialog.schedule_mode_switch_unavailable")}`}
        description={t(
          "common:button.description.schedule_mode_switch_unavailable"
        )}
        notices={t("common:button.notices.schedule_mode_switch_unavailable", {
          returnObjects: true,
        })}
        buttons={[
          {
            label: `${t("common:button.close")}`,
            onClick: () =>
              modeSwitch.dialogStates.setOpenScheduleModeSwitchUnavailable(
                false
              ),
          },
        ]}
        handleClose={() =>
          modeSwitch.dialogStates.setOpenScheduleModeSwitchUnavailable(false)
        }
      />
    </>
  );
};

function MyPage() {
  const navigate = useNavigate();
  const { soulId, lng, work, contentWork, character } = useUrlParams();
  const { t } = useTranslation(["common", contentWork]);
  const backend = useSoulBackend();

  const { userEpisode, isLoading, afterPurchase, isError } =
    useUserEpisode(soulId, backend);
  const modeSwitch = useModeSwitch(userEpisode, soulId, t, lng, backend);
  const resetMemory = useResetMemory(userEpisode, soulId);
  const requestFormUrl = getRequestFormUrl(lng);

  // Navigation handlers
  const handleMemoryRollback = () => {
    window.open(memoryRollbackUrls[lng] || memoryRollbackUrls["ja"], "_blank");
  };

  const handleNavigateQuests = () => {
    navigate(withLng(`/${work}/quests?soul_id=${soulId}`, lng));
  };

  const handleNavigateEmailSettings = () => {
    navigate(withLng(`/${work}/email-settings?soul_id=${soulId}`, lng));
  };

  const handleCloseWindow = () => {
    liff.closeWindow();
  };

  const handleRequestForm = () => {
    window.open(requestFormUrl, "_blank");
  };

  const onRequestAdjustment = () => {
    window.open(
      "https://docs.google.com/forms/d/e/1FAIpQLSffVNr6DctPsN8elTa0GRYnr3nx2ZqcETaBuSTtTCRVvvRtDQ/viewform",
      "_blank"
    );
  };

  const onPromote = () => {
    navigate(withLng(`/${work}/tickets?soul_id=${soulId}`, lng));
  };

  if (isLoading) {
    return (
      <AppLayout page="MyPage">
        <Loading />
      </AppLayout>
    );
  }

  if (isError) {
    return (
      <AppLayout page="MyPage">
        <Error />
        <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
          <FaqButton work={contentWork} lng={lng} />
        </Box>
      </AppLayout>
    );
  }

  if (!afterPurchase) {
    return (
      <AppLayout page="MyPage">
        <TicketNotFound onClick={onPromote} />
        <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
          <FaqButton work={contentWork} lng={lng} />
        </Box>
      </AppLayout>
    );
  }

  return (
    <AppLayout page="MyPage">
      <Box
        sx={{
          display: "flex",
          paddingTop: 4,
          paddingBottom: 4,
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 1,
            marginBottom: 2,
          }}
        >
          <Typography
            sx={(theme) => ({
              ...theme.components.pageHeader,
            })}
          >
            Character
          </Typography>
        </Box>

        <MyPageCard userEpisode={userEpisode} soulName={character} />

        {/* 1. 調整依頼ボタン（将来的に使用予定） */}
        {false && (
          <StyledButton
            variant="contained"
            sx={{
              marginTop: 4,
              background: modeSwitch.isProcessing ? "white" : "transparent",
            }}
            onClick={onRequestAdjustment}
          >
            {t("common:button.request_adjustment")}
          </StyledButton>
        )}

        {/* 2. チケット購入ページへ（期限切れ時のみ） */}
        {userEpisode.is_expired && (
          <StyledButton
            variant="contained"
            sx={{
              marginTop: 4,
              background: modeSwitch.isProcessing ? "white" : "transparent",
            }}
            onClick={onPromote}
          >
            {t("common:button.purchase_episode")}
          </StyledButton>
        )}

        {/* 2'. 期間の延長（延長できるチケットを使っている間だけ） */}
        {userEpisode.can_extend && (
          <StyledButton
            variant="contained"
            sx={{
              marginTop: 4,
              background: modeSwitch.isProcessing ? "white" : "transparent",
            }}
            onClick={onPromote}
          >
            {t("common:button.extend_ticket")}
          </StyledButton>
        )}

        {/* 3. キャラクターの状況 */}
        <CharacterSituationSection
          t={t}
          work={contentWork}
          character={character}
          isProcessing={modeSwitch.isProcessing}
        />

        {/* 4. 記憶リセット（期限内のみ） */}
        <ResetMemorySection
          userEpisode={userEpisode}
          resetMemory={resetMemory}
          t={t}
          isProcessing={modeSwitch.isProcessing}
        />

        {/* 5. モード切り替え（期限内かつサポート対象のみ） */}
        <ModeSwitchSection
          userEpisode={userEpisode}
          modeSwitch={modeSwitch}
          t={t}
          lng={lng}
          isProcessing={modeSwitch.isProcessing}
        />

        {/* 5. 記憶の巻き戻し依頼 */}
        <StyledButton
          variant="contained"
          sx={{ mt: 4 }}
          onClick={handleMemoryRollback}
        >
          {t("common:button.memory_rollback")}
        </StyledButton>

        {/* 6. クエスト一覧 */}
        <StyledButton
          variant="contained"
          sx={{ mt: 4 }}
          onClick={handleNavigateQuests}
        >
          {t("common:button.quests")}
        </StyledButton>

        <FaqButton work={contentWork} lng={lng} sx={{ mt: 4 }} />

        {requestFormUrl && (
          <StyledButton
            variant="contained"
            sx={{ mt: 4 }}
            onClick={handleRequestForm}
          >
            {t("common:button.request_form")}
          </StyledButton>
        )}

        {/* 7. チャットに戻る */}
        <StyledButton
          variant="contained"
          sx={{ mt: 4 }}
          onClick={handleCloseWindow}
        >
          {t("common:button.back_to_chat")}
        </StyledButton>

        {/* 8. メール設定 */}
        <StyledButton
          variant="contained"
          sx={{ mt: 4, mb: 4 }}
          onClick={handleNavigateEmailSettings}
        >
          {t("common:button.email_settings")}
        </StyledButton>
      </Box>
    </AppLayout>
  );
}

export default MyPage;
